import { useState } from "react";
import { toast } from "sonner";
import DocsContent from "../../components/DocsContent";
import PricingTable from "../../components/PricingTable";
import { Card, ErrorBanner, Notice } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { useSeo } from "../../lib/seo";

const Wrap = ({ eyebrow, title, children }) => (
  <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-purple-400/90">{eyebrow}</div>
    <h1 className="mb-10 text-4xl font-semibold text-white sm:text-5xl" data-testid="page-title">{title}</h1>
    {children}
  </div>
);

export function Pricing() {
  const { lang } = useT();
  const fi = lang === "fi";
  useSeo({ title: fi ? "Hinnoittelu — aloita ilmaiseksi, maksa kasvaessasi" : "Pricing — start free, pay as you grow",
    description: fi ? "1 000 sähköpostia kuukaudessa ilmaiseksi. Pro 45 €/kk, Scale 145 €/kk ja räätälöity volyymihinnoittelu. Ei sitoutumista." : "1,000 emails a month free. Pro €45/month, Scale €145/month and custom volume pricing for newsletters, campaigns and business email. Cancel any time.", path: "/pricing" });
  return (
    <div className="relative overflow-x-hidden"><div className="aurora opacity-30" />
      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mb-14 text-center"><div className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-300">{fi ? "Hinnoittelu" : "Pricing"}</div>
          <h1 className="mt-4 text-4xl font-semibold text-white sm:text-6xl" data-testid="page-title">{fi ? "Aloita ilmaiseksi." : "Start free."} <span className="font-serif-lux italic">{fi ? "Kasva rauhassa." : "Grow at your pace."}</span></h1>
          <p className="mx-auto mt-5 max-w-xl text-zinc-400">{fi ? "Jokainen paketti sisältää kampanjat, tekoälyavun, rehelliset raportit ja oman verkkotunnuksesi. Valitse volyymi, joka sopii sinulle nyt — vaihda milloin tahansa." : "Every plan includes campaigns, AI writing help, honest reports and sending from your own domain. Pick the volume that fits today — change any time."}</p></div>
        <PricingTable />
      </div></div>
  );
}

export function PublicDocs() {
  useSeo({ title: "Email API documentation", description: "Send email from your app with the CARIO Mail REST API: authentication, sending, attachments, contacts, statuses, webhooks and SMTP.", path: "/docs" });
  return <Wrap eyebrow="Documentation" title="REST API reference"><DocsContent /></Wrap>;
}

export function Support() {
  const { brand } = useAuth();
  useSeo({ title: "Support and abuse reports", description: "Get help with CARIO Mail or report an unwanted email sent through our platform. We read every message.", path: "/support" });
  const [form, setForm] = useState({ reporter_email: "", message_id: "", details: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post("/public/abuse-reports", { ...form, message_id: form.message_id || null });
      setDone(true); toast.success("Report received");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Wrap eyebrow="Support" title="Support and abuse reports">
      <div className="grid gap-6 lg:grid-cols-2">
        <Card testId="support-contact">
          <h2 className="mb-3 text-lg font-semibold text-white">Contact</h2>
          {brand.support_email ? <a className="text-purple-300" href={`mailto:${brand.support_email}`}>{brand.support_email}</a>
            : <p className="text-sm text-zinc-400">A support contact has not been configured yet.</p>}
          <h2 className="mb-3 mt-8 text-lg font-semibold text-white">Status</h2>
          {brand.status_url ? <a className="text-purple-300" href={brand.status_url}>{brand.status_url}</a>
            : <p className="text-sm text-zinc-400">A public status page has not been published yet.</p>}
        </Card>
        <Card testId="abuse-form-card">
          <h2 className="mb-1 text-lg font-semibold text-white">Report abuse</h2>
          <p className="mb-5 text-sm text-zinc-400">Received unwanted email sent through this platform? Tell us.</p>
          {done ? <Notice tone="ok" title="Thank you" testId="abuse-success">Your report was received and will be reviewed.</Notice> : (
            <form onSubmit={submit} className="space-y-4">
              <ErrorBanner error={error} />
              <div className="space-y-1.5"><Label htmlFor="ab-email">Your email</Label>
                <Input id="ab-email" type="email" required value={form.reporter_email} onChange={(e) => setForm({ ...form, reporter_email: e.target.value })} data-testid="abuse-email" /></div>
              <div className="space-y-1.5"><Label htmlFor="ab-mid">Message ID (optional, from the X-CARIO-Message-ID header)</Label>
                <Input id="ab-mid" value={form.message_id} onChange={(e) => setForm({ ...form, message_id: e.target.value })} data-testid="abuse-message-id" /></div>
              <div className="space-y-1.5"><Label htmlFor="ab-d">Details</Label>
                <Textarea id="ab-d" required minLength={10} rows={4} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} data-testid="abuse-details" /></div>
              <Button type="submit" disabled={busy} data-testid="abuse-submit">{busy ? "Sending…" : "Submit report"}</Button>
            </form>
          )}
        </Card>
      </div>
    </Wrap>
  );
}
